'use client';

import type { ProductUnitItem } from '@weberus/shared-types';
import { omit } from 'lodash-es';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { waitUntilClickTimeout } from '@weberus/utils';
import { ProductUnitGroup } from '@/app/(pages)/results/ui/product-list/product-unit-group';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';
import { isFeatureEnabled } from '@/app/_shared/utils/is-feature-enabled';

export const RecommendList = ({
  header,
  items,
  position = 0,
  isNoResult,
  serverLogMeta = {},
}: {
  header: {
    title?: string;
    keyword?: string;
    subTitle?: string;
  };
  items: ProductUnitItem[];
  position?: number;
  isNoResult?: boolean;
  serverLogMeta: Record<string, unknown>;
}) => {
  if (!header?.title || !items.length) return null;

  const isUniversalSearchV1 = isFeatureEnabled('universalSearchV1');

  // 추천 리스트는 feature 영역 미노출을 위해 description 필드 제거
  const itemsWithoutFeature = items.map((item) => ({ ...item, data: omit(item.data, 'descriptions') }));

  // position : 위젯 내 상품 순서
  // listPosition : 전체 리스트 내 상품 순서
  const handleImpressionLog = (item: ProductUnitItem, index: number, listPosition: number) => {
    clientLogProtobuf(ProductListPageLog.RecommendSectionCardWidgetItem.impression, {
      logMeta: {
        ...serverLogMeta,
        ...item.serverLogMeta,
      },
      position: index + 1,
      listPosition: listPosition + index + 1,
      noAvailablePlaceMsg: isNoResult,
    })();
  };

  const handleClickLog = async (item: ProductUnitItem, index: number, listPosition: number) => {
    await waitUntilClickTimeout(
      ProductListPageLog.RecommendSectionCardWidgetItem.click({
        logMeta: {
          ...serverLogMeta,
          ...item.serverLogMeta,
        },
        position: index + 1, // position : 위젯 내 상품 순서
        listPosition: listPosition + index + 1, // listPosition : 전체 리스트 내 상품 순서
        noAvailablePlaceMsg: isNoResult,
      }),
    );
    navigateTo(item?.data?.action);
  };

  return (
    <>
      {isUniversalSearchV1 ? (
        <div className="flex whitespace-nowrap px-16 pt-26 pc:px-0">
          <span className="text-text-neutral-main typography-subtitle-18-bold">‘</span>
          <span className="text-text-primary-main typography-subtitle-18-bold">{header?.keyword || ''}</span>
          <span className="text-text-neutral-main typography-subtitle-18-bold">
            {'‘ '}
            검색한 고객들이 이런 상품도 봤어요{' '}
          </span>
        </div>
      ) : (
        <div className="flex flex-col px-16 pt-26 pc:px-0">
          <p className="typography-subtitle-18-bold">{header?.title}</p>
          <div className="flex w-full overflow-hidden truncate">
            <span>‘</span>
            <span className="truncate typography-body-13-regular">{header?.keyword}</span>
            <span>’</span>
            <span className="ml-2 typography-body-13-regular">{header?.subTitle}</span>
          </div>
        </div>
      )}
      <div className="flex w-full flex-col bg-static-white [&>*:first-child]:border-t-0 pc:[&>:first-child]:mt-20">
        <ProductUnitGroup
          items={itemsWithoutFeature}
          listPosition={position}
          onImpression={handleImpressionLog}
          onClick={handleClickLog}
        />
      </div>
    </>
  );
};
